import React, { useEffect, useState } from 'react';
import { ClickableAreaRegularIcon, FloatCenterRegularIcon } from 'src/monet/icons';
import { Block, CaptionableBlockContent, LinkBlockContent } from '../types';
import { LinkControlPanelInputForm } from './link-input-form';

import { LinkResponse, LinkDisplayOption } from './types';
import { TooltipComponent } from 'src/common/tooltip/component';
import { ControlPanelToggleButton } from '../components/control-panel/toggle-button';
import { ToggleBlockCaption } from '../components/toggle-block-caption';
import { ControlPanelSwitchButton } from '../components/control-panel/switch';
import { TogglePersonalContent } from '../components/toggle-personal-content';

type Props = {
  block: Block;
  onNewUrl: (url: string) => void;
  onDisplayOptionChange: (link: LinkResponse, displayOption: LinkDisplayOption) => void;
  onShowImageChange: (hideImage: boolean) => void;
  onOpenInNewTabChange: (openInNewTab: boolean) => void;
};

export const EditLinkControlPanelContent = ({
  block,
  onNewUrl,
  onDisplayOptionChange,
  onShowImageChange,
  onOpenInNewTabChange,
}: Props) => {
  const content = block.content as CaptionableBlockContent<LinkBlockContent>;
  const url = content?.link?.transformed_url || '';

  const [hiddenNavBar, setHiddenNavBar] = useState<boolean>(url.includes('rm=minimal'));

  let urlParams = {
    ...(hiddenNavBar ? { rm: 'minimal' } : {}),
  };

  useEffect(() => {
    if (url.includes('rm=minimal')) {
      setHiddenNavBar(true);
    }
  }, [url]);

  if (block.content.type !== 'link') return null;

  const { displayOption, openInNewTab, hideImage, link } = block.content as LinkBlockContent;

  const showHideImageOption = (link?.thumbnail && link?.thumbnail?.url) || link.url.includes('app.box');
  const showImageValue = showHideImageOption ? !hideImage : false;

  const isNotionLink = link.transformed_url.includes('notion-proxy') || link.transformed_url.includes('notion.site') || link.url.includes('notion.site');
  const canLinkEmbed = link?.iframe_safe || link?.third_party_iframe_safe || isNotionLink || false;
  const hasOpenInNewTabValue = typeof openInNewTab !== 'undefined';
  const openInNewTabValue = canLinkEmbed ? (hasOpenInNewTabValue ? openInNewTab : false) : true;

  let canLinkEmbedCopy = 'This content cannot be embedded inline';
  if (canLinkEmbed) {
    canLinkEmbedCopy = 'Display this content within your journey';
  }

  const placeholder = isNotionLink ? 'Paste your URL / iframe code' : 'Paste a URL';

  return (
    <div className='flex flex-col gap-4'>
      <LinkControlPanelInputForm url={url} onNewUrl={onNewUrl} urlParams={urlParams} placeholder={placeholder} />
      <div className='flex flex-col space-y-2'>
        <div className='flex gap-2'>
          <TooltipComponent
            tooltipContent={canLinkEmbedCopy}
            placement='left'
            styling='tooltip'
            theme='journey'
            offset={24}
            anchorClassName='flex-1'
            containerClassName='flex-1'
          >
            <ControlPanelToggleButton
              label='Inline'
              disabled={!canLinkEmbed}
              selected={displayOption === 'inline'}
              onClick={() => onDisplayOptionChange(link, 'inline')}
              icon={<FloatCenterRegularIcon />}
            />
          </TooltipComponent>
          <TooltipComponent
            tooltipContent={'Display this content behind a button'}
            placement='left'
            styling='tooltip'
            theme='journey'
            offset={104 + 24 + 8}
            anchorClassName='flex-1'
            containerClassName='flex-1'
          >
            <ControlPanelToggleButton
              label='Button'
              selected={displayOption === 'new-tab'}
              onClick={() => onDisplayOptionChange(link, 'new-tab')}
              icon={<ClickableAreaRegularIcon />}
            />
          </TooltipComponent>
        </div>
        <TogglePersonalContent
          block={block}
          contentObject={{
            link_url: url,
          }}
        />
        {displayOption === 'new-tab' ? (
          <>
            <TooltipComponent
              tooltipContent={!canLinkEmbed ? 'This link can only be opened in a new tab' : ''}
              placement='left'
              styling='tooltip'
              theme='journey'
              offset={24}
              anchorClassName='flex-1'
              containerClassName='flex-1'
            >
              <ControlPanelSwitchButton
                checked={openInNewTabValue}
                disabled={!canLinkEmbed}
                onChange={(checked: boolean) => onOpenInNewTabChange(checked)}
                label='Open in a new tab'
              />
            </TooltipComponent>
            <TooltipComponent
              tooltipContent={!showHideImageOption ? 'This link has no associated image' : ''}
              placement='left'
              styling='tooltip'
              theme='journey'
              offset={24}
              anchorClassName='flex-1'
              containerClassName='flex-1'
            >
              <ControlPanelSwitchButton
                checked={showImageValue}
                disabled={!showHideImageOption}
                onChange={(checked: boolean) => onShowImageChange(checked)}
                label='Show image'
              />
            </TooltipComponent>
          </>
        ) : (
          <ToggleBlockCaption block={block} />
        )}
        {url.startsWith('https://docs.google.com/presentation/') && url.includes('embed') && (
          <ControlPanelSwitchButton
            label='Remove nav bar'
            checked={hiddenNavBar}
            onChange={() => setHiddenNavBar(!hiddenNavBar)}
          />
        )}
      </div>
    </div>
  );
};
